'use client';

import { useFormContext } from 'react-hook-form';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CITIZENSHIP_OPTIONS, ENTITY_TYPES } from '../constants/sale.constants';
import type { SaleApplicationData } from '@/features/original-sale';
import { cn } from '@/lib/utils';
import { SaleDateMaskedInput } from './SaleDateMaskedInput';

export const SalePersonalFieldsStepTwo = () => {
  const { control } = useFormContext<SaleApplicationData>();

  return (
    <div className="space-y-4">
      <FormField
        control={control}
        name="entityType"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormControl>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <SelectTrigger className="h-10 rounded-[10px]" error={!!fieldState.error}>
                  <SelectValue placeholder="Тип лица" />
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="lastName"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel className={cn('font-normal', !fieldState.error && 'text-[#888C91]')}>
              Фамилия<span>*</span>
            </FormLabel>
            <FormControl>
              <Input className="h-10 font-medium" placeholder="Введите фамилию" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="firstName"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel className={cn('font-normal', !fieldState.error && 'text-[#888C91]')}>
              Имя<span>*</span>
            </FormLabel>
            <FormControl>
              <Input className="h-10 font-medium" placeholder="Введите имя" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="middleName"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel className={cn('font-normal', !fieldState.error && 'text-[#888C91]')}>
              Отчество<span>*</span>
            </FormLabel>
            <FormControl>
              <Input className="h-10 font-medium" placeholder="Введите отчество" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="citizenship"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel className={cn('font-normal', !fieldState.error && 'text-[#888C91]')}>
              Гражданство<span>*</span>
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <SelectTrigger className="h-10 rounded-[10px]" error={!!fieldState.error}>
                  <SelectValue placeholder="Выберите гражданство" />
                </SelectTrigger>
                <SelectContent>
                  {CITIZENSHIP_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="passportNumber"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel className={cn('font-normal', !fieldState.error && 'text-[#888C91]')}>
              Серия и номер паспорта<span>*</span>
            </FormLabel>
            <FormControl>
              <Input className="h-10 font-medium" placeholder="MP567890" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          name="passportIssuedBy"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel className={cn('font-normal', !fieldState.error && 'text-[#888C91]')}>
                Кем выдан<span>*</span>
              </FormLabel>
              <FormControl>
                <Input className="h-10 font-medium" placeholder="Название органа" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="passportIssuedDate"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel className={cn('font-normal', !fieldState.error && 'text-[#888C91]')}>
                Дата выдачи<span>*</span>
              </FormLabel>
              <FormControl>
                <SaleDateMaskedInput
                  className="h-10 font-medium"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={control}
        name="birthDate"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel className={cn('font-normal', !fieldState.error && 'text-[#888C91]')}>
              Дата рождения<span>*</span>
            </FormLabel>
            <FormControl>
              <SaleDateMaskedInput
                className="h-10 font-medium"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
                ref={field.ref}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
